/*
 * Structure Helper service is used to compliment and take additional tasks from Structure Service:
 * 
 */

import { Injectable } from '@angular/core';
import { combineLatest, Observable, of } from 'rxjs';
import { StructureService } from './structure.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { distinctUntilChanged, filter, map, shareReplay } from 'rxjs/operators';
import { ConfigService } from '../config/config.service';
import { stringToNumber } from '../codeUtils';
import { CategoryInterface, GameInterface } from 'src/app/interfaces/lobby.interface';

@UntilDestroy()
@Injectable({
  providedIn: 'root'
})
export class StructureHelperService {

  private allGames$: Observable<GameInterface[]> | undefined;
  private allGamesCache: GameInterface[] = [];
  private allGamesLoaded: boolean = false;
  private homeCategory$: Observable<CategoryInterface | undefined> | undefined;
  private promotionCategory$: Observable<CategoryInterface | undefined> | undefined;

  private lgScreenGrid: number;
  private mdScreenGrid: number;
  private smScreenGrid: number;
  public xsScreenGrid: number;

  constructor(
    private structure: StructureService,
    private config: ConfigService,
  ) {
    this.lgScreenGrid = stringToNumber(this.config?.getConfig()['FEATURE_LOBBY_LANE_LGSCREEN_TILESPERSCREEN']) || 6;
    this.mdScreenGrid = stringToNumber(this.config?.getConfig()['FEATURE_LOBBY_LANE_MDSCREEN_TILESPERSCREEN']) || 5;
    this.smScreenGrid = stringToNumber(this.config?.getConfig()['FEATURE_LOBBY_LANE_SMSCREEN_TILESPERSCREEN']) || 4;
    this.xsScreenGrid = stringToNumber(this.config?.getConfig()['FEATURE_LOBBY_LANE_XSSCREEN_TILESPERSCREEN']) || 2.8;
  }

  public lobbyLaneSizeByMediaQuery(mdMediaQuery: MediaQueryList, smMediaQuery: MediaQueryList, xsMediaQuery: MediaQueryList) {
    if (mdMediaQuery.matches && !smMediaQuery.matches) {
      return this.mdScreenGrid;
    } else if (smMediaQuery.matches && !xsMediaQuery.matches) {
      return this.smScreenGrid;
    } else if (xsMediaQuery.matches) {
      return this.xsScreenGrid;
    } else {
      return this.lgScreenGrid;
    }
  }

  public getMainLobbyName(): Observable<string | undefined> {
    return this.structure.getCategoriesByPlacement('MAIN').pipe(
      map((categories: CategoryInterface[]) => categories?.[0]?.name),
    );
  }

  /*
   * All Games Section Starts Here:
   * use loadAllGames() to load all games on as it is cached data
   * use getAllGames() to get all games from structure using placement MAIN and if category code contains 'ALLGAMES'
   * Use getGameById() to get single games using its id
   * Get array of gake data based on array of ids filterGamesWithListBasedOnIds()
   */

  public getAllGames(): Observable<GameInterface[]> {
    if (!this.allGames$) {
      this.allGames$ = this.structure.getCategoriesByPlacement('MAIN').pipe(
        map((categories: CategoryInterface[]) => {
          const targetCategory = categories?.find(
            cat => cat.category_code?.includes('ALLGAMES')
          );
          return targetCategory?.games || [];
        }),
        shareReplay(1)
      );
    }
    return this.allGames$;
  }

  public loadAllGames(): void {
    if (!this.allGamesLoaded) {
      this.getAllGames()
        .pipe(untilDestroyed(this))
        .subscribe(games => {
          this.allGamesCache = games;
          this.allGamesLoaded = true;
        });
    }
  }

  public getGameById(id: number): GameInterface | undefined {
    return this.allGamesCache.find(game => game.id == id);
  }

  filterGamesWithListBasedOnIds(gameFilterArray: any[]) {
    const tempArray = [...gameFilterArray];
    if (tempArray) {
      let gameArray = [];
      for (let gameInner of tempArray) {
        let value = this.allGamesCache.find(value => gameInner.id == value.id);
        if (value) gameArray.push(value);
      }
      return of(gameArray);
    } else {
      return of([]);
    };
  }

  //------------- All Games Section Ends --------------

  public findCategoryById(id: number): Observable<CategoryInterface | undefined> {
    return this.structure.structure$.pipe(
      map((allStructures) => {
        for (const s of allStructures) {
          const cats = s.categoryInfo || [];
          const found = cats.find(cat => cat.id === id);
          if (found) {
            return found;
          }
        }
        return undefined;
      })
    );
  }

  //------------- Default Category --------------

  public getDefaultCategory(): Observable<CategoryInterface | undefined> {
    return combineLatest([
      this.structure.structure$,
      this.getHomeCategory()
    ]).pipe(
      untilDestroyed(this),
      filter(([structures, homeCategory]) =>
        structures.length > 0
      ),
      map(([structures, homeCategory]) => {
        const id = isNaN(Number(homeCategory?.action_param)) ? undefined : Number(homeCategory?.action_param);


        if (id == null || isNaN(id)) {
          return structures.find(s => s.placement === 'MAIN')?.categoryInfo?.[0];
        }

        for (const structure of structures) {
          const category = structure.categoryInfo?.find(cat => cat.id === id);
          if (category) {
            return category;
          }
        }

        return structures.find(s => s.placement === 'MAIN')?.categoryInfo?.[0];
      }),
      distinctUntilChanged((a, b) => a?.id === b?.id)
    );
  }

  //Get Home Category

  public getHomeCategory(): Observable<CategoryInterface | undefined> {
    if (!this.homeCategory$) {
      this.homeCategory$ = this.structure.structure$.pipe(
        filter(structures => structures.length > 0),
        map(structures => {
          for (const structure of structures) {
            const category = structure.categoryInfo?.find(cat => cat.action_type === 'home');
            if (category) return category;
          }
          return undefined;
        }),
        distinctUntilChanged((a, b) => a?.id === b?.id),
        shareReplay(1)
      );
    }

    return this.homeCategory$;
  }

  public get mainCasinoName(): Observable<string | undefined> {
    return this.structure.structure$.pipe(
      map(structures => structures.find(s => s.placement === 'MAIN')?.name)
    );
  }

    public getPromotionCategory(): Observable<CategoryInterface | undefined> {
    this.promotionCategory$ = this.structure.structure$.pipe(
      filter(structures => structures.length > 0),
      map(structures => {
        for (const structure of structures) {
          const category = structure.categoryInfo?.find(cat => cat.action_type === 'promotions');
          if (category) return category;
        }
        return undefined;
      }),
      distinctUntilChanged((a, b) => a?.id === b?.id),
      shareReplay(1)
    );


    return this.promotionCategory$;
  }
}
